import { c as _c } from "react/compiler-runtime";
import { getErrorMessage } from '@onefootprint/fetch-hosted';
import { getHostedOnboardingConfigOptions } from '@onefootprint/fetch-hosted/queries';
import { getLogger, setTenantLogContext, trackAction, useBusiness } from '@onefootprint/idv';
import type { OnboardingSessionConfig } from '@onefootprint/request-types';
import { Shimmer } from '@onefootprint/ui';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import useHostedMachine from 'src/hooks/use-hosted-machine';
import useParseUrl from './use-url-params';
const {
  logError,
  logInfo
} = getLogger({
  location: 'hosted-init'
});
const setupLogger = (config: OnboardingSessionConfig) => {
  setTenantLogContext(config, {
    appClipExperienceId: config.appClipExperienceId,
    isAppClipEnabled: config.isAppClipEnabled,
    isInstantAppEnabled: config.isInstantAppEnabled,
    kind: String(config.kind)
  });
};
const Init = () => {
  const $ = _c(27);
  const [state, send] = useHostedMachine();
  const {
    obConfigAuth,
    authToken
  } = state.context;
  let t0;
  if ($[0] !== obConfigAuth) {
    t0 = {
      obConfigAuth
    };
    $[0] = obConfigAuth;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const queryGetBusiness = useBusiness(t0);
  const isGetBusinessLoading = queryGetBusiness.isFetching && queryGetBusiness.isPending;
  const businessBoKycData = queryGetBusiness.data;
  let t1;
  if ($[2] !== authToken || $[3] !== obConfigAuth) {
    t1 = getHostedOnboardingConfigOptions({
      headers: obConfigAuth ?? {
        "X-Fp-Authorization": authToken
      }
    });
    $[2] = authToken;
    $[3] = obConfigAuth;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const t2 = !!obConfigAuth || !!authToken;
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = {
      ...t1,
      enabled: t2
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const queryGetOnboardingConfig = useQuery(t3);
  const isGetOnboardingConfigLoading = queryGetOnboardingConfig.isFetching && queryGetOnboardingConfig.isPending;
  let t4;
  if ($[8] !== send) {
    t4 = {
      onSuccess: t5 => {
        const {
          obConfigAuth: parsedObConfigAuth,
          authToken: parsedAuthToken,
          urlType
        } = t5;
        logInfo(`URL parsed with type: ${urlType}`);
        send({
          type: "initContextUpdated",
          payload: {
            authToken: parsedAuthToken,
            obConfigAuth: parsedObConfigAuth,
            urlType
          }
        });
      },
      onError: err => {
        logError("Error parsing URL", err);
        send({
          type: "errorReceived",
          payload: {
            error: err
          }
        });
      }
    };
    $[8] = send;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  useParseUrl(t4);
  let t5;
  if ($[10] !== businessBoKycData || $[11] !== isGetBusinessLoading || $[12] !== isGetOnboardingConfigLoading || $[13] !== queryGetBusiness.error || $[14] !== queryGetOnboardingConfig.data || $[15] !== queryGetOnboardingConfig.error || $[16] !== send) {
    t5 = () => {
      if (isGetBusinessLoading || isGetOnboardingConfigLoading) {
        return;
      }
      const businessError = queryGetBusiness.error;
      const configError = queryGetOnboardingConfig.error;
      const error = businessError || configError;
      if (error) {
        if (businessError) {
          logError(`Fetching business details: ${getErrorMessage(businessError)}`, businessError);
        }
        if (configError) {
          logError(`Fetching onboarding config: ${getErrorMessage(configError)}`, configError);
        }
        send({
          type: "errorReceived",
          payload: {
            error
          }
        });
        return;
      }
      const config = queryGetOnboardingConfig.data;
      if (config?.recordRumSession) {
        setupLogger(config);
      }
      if (businessBoKycData || config) {
        trackAction("hosted:started");
        send({
          type: "initContextUpdated",
          payload: {
            businessBoKycData,
            onboardingConfig: config
          }
        });
      }
    };
    $[10] = businessBoKycData;
    $[11] = isGetBusinessLoading;
    $[12] = isGetOnboardingConfigLoading;
    $[13] = queryGetBusiness.error;
    $[14] = queryGetOnboardingConfig.data;
    $[15] = queryGetOnboardingConfig.error;
    $[16] = send;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== businessBoKycData || $[19] !== isGetBusinessLoading || $[20] !== isGetOnboardingConfigLoading || $[21] !== queryGetBusiness.error || $[22] !== queryGetOnboardingConfig.data || $[23] !== queryGetOnboardingConfig.error) {
    t6 = [isGetBusinessLoading, isGetOnboardingConfigLoading, queryGetBusiness.error, queryGetOnboardingConfig.error, queryGetOnboardingConfig.data, businessBoKycData];
    $[18] = businessBoKycData;
    $[19] = isGetBusinessLoading;
    $[20] = isGetOnboardingConfigLoading;
    $[21] = queryGetBusiness.error;
    $[22] = queryGetOnboardingConfig.data;
    $[23] = queryGetOnboardingConfig.error;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  useEffect(t5, t6);
  let t7;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Avatar />;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="flex flex-col gap-6 justify-center items-center md:pt-6"><div className="flex flex-col justify-center items-center gap-4">{t7}<div className="flex flex-col justify-center items-center gap-2 mt-4"><Title /><Subtitle /></div></div><Button /></div>;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  return t8;
};
const Title = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Shimmer height="28px" width="300px" className="max-w-full" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const Subtitle = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Shimmer height="24px" width="350px" className="max-w-full" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const Button = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Shimmer height="48px" width="100%" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const Avatar = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Shimmer height="72px" width="72px" className="rounded-full" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
export default Init;